// JornadaConfirmarDialog.jsx
//
// #1887 · confirmar la activación de una jornada (spec `.claude/specs/jornada-suelta/`, T25), GLOBAL (montado una sola vez
// en `App.js`) porque lo abren dos caminos: el botón de la banda de /turno (`jornada-boton`, `jornada-activar-otra`) y un
// 403 `requiere_boton` / `ventana_terminada` de cualquier punto de IA, también fuera de /turno (`JornadaAvisosGate`).
//
// Antes de gastar saldo el médico ve la hora exacta de término y confirma (decisión de Sebastián, 30-oct-2026): al aceptar
// se manda la OPCIÓN elegida y la HORA QUE VIO (`termina_en_visto`). El backend abre la ventana que cierra exactamente a esa
// hora; si pasaron más de 5 minutos responde 409 `hora_cambio` con la hora nueva, SIN consumir nada, y acá se la muestra
// con un aviso. El médico nunca recibe una hora de término distinta de la que confirmó (R45, D2).
// R75 (v5, mockup `eleccion_tipo_*.png`): con más de una opción, apretar Activar abre primero «Elige qué jornada activar»
// (elegir no descuenta nada) y recién después la confirmación con la hora grande de la elegida. Con una sola opción va
// directo a confirmar. Las opciones y sus horas las decide el backend (`opciones_activar`); acá no se calcula ninguna.
// Cubre R58, R62, R75 — .claude/specs/jornada-suelta/
import { useEffect, useState } from "react";
import { Box, Button, Dialog, Stack, Typography } from "@mui/material";
import useJornadaEstado from "../../hooks/useJornadaEstado";
import {
  EVENTO_ABRIR_CONFIRMAR,
  activarJornada,
  cuando,
  diaMes,
  fechaLarga,
  guardadas,
  hora,
  horasDe,
  offsetMin,
  opcionesDe,
  saldoDe,
} from "../../helpers/suelta-api";
import { FUENTE, ghostSx, paperSx, primarySx, Z_ENCIMA_DEL_GATE } from "./estilos";

const Punto = ({ children }) => (
  <Box component="li" sx={{ fontFamily: FUENTE, fontSize: 13.5, lineHeight: 1.4, color: "var(--text-secondary)", mb: 0.75, "&:last-child": { mb: 0 } }}>
    {children}
  </Box>
);

const Recuadro = ({ testId, children }) => (
  <Box
    data-testid={testId}
    role="status"
    sx={{
      fontFamily: FUENTE,
      fontSize: 13,
      lineHeight: 1.4,
      color: "var(--text-primary)",
      p: 1.25,
      mb: 1.5,
      borderRadius: "10px",
      border: "1px solid rgba(var(--color-warning-rgb),0.4)",
      backgroundColor: "rgba(var(--color-warning-rgb),0.1)",
    }}
  >
    {children}
  </Box>
);

const HoraGrande = ({ iso, ahora, horas }) => {
  const dia = cuando(iso, ahora);
  const simple = /^(de hoy|de mañana)$/.test(dia);
  return (
    <Box sx={{ p: 1.5, mb: 1.5, borderRadius: "14px", backgroundColor: "rgba(var(--color-primary-rgb),0.1)", border: "1px solid rgba(var(--color-primary-rgb),0.35)" }}>
      <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: "var(--text-secondary)" }}>Generas documentos y usas la IA hasta las</Typography>
      <Typography
        data-testid="jornada-confirmar-hora"
        sx={{ fontFamily: FUENTE, fontSize: 38, fontWeight: 700, lineHeight: 1.15, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}
      >
        {hora(iso)}
      </Typography>
      <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: "var(--text-secondary)" }}>
        {`${dia.replace(/^de /, "").replace(/^del /, "")}${simple ? `, ${fechaLarga(iso)}` : ""}`}
      </Typography>
      <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: "var(--text-muted)", mt: 0.5 }}>{`${horas} horas más 1 de cortesía, desde las ${hora(ahora)}.`}</Typography>
    </Box>
  );
};

const MENSAJES = {
  opcion_invalida: "Esa jornada ya no se puede activar. Actualizamos tus opciones.",
  sin_saldo: "Ya no te quedan jornadas para activar.",
  abierta: "Ya tienes una jornada activa.",
};

const JornadaConfirmarDialog = () => {
  const { jornada, recargar } = useJornadaEstado({ auto: false });
  const [abierto, setAbierto] = useState(false);
  const [elegida, setElegida] = useState(null);
  const [override, setOverride] = useState(null); // la hora nueva de un 409 `hora_cambio`
  const [cambio, setCambio] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [ahora, setAhora] = useState(() => new Date());

  const opciones = opcionesDe(jornada);

  useEffect(() => {
    const abrir = (event) => {
      const pedida = event?.detail?.opcion || null;
      setAhora(new Date());
      setOverride(null);
      setCambio(false);
      setError("");
      setElegida(pedida);
      setAbierto(true);
    };
    window.addEventListener(EVENTO_ABRIR_CONFIRMAR, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_CONFIRMAR, abrir);
  }, []);

  // Con una sola opción se salta la elección (R58); con varias se elige primero (R75).
  const unica = opciones.length === 1 ? opciones[0] : null;
  const opcionActual = elegida || unica?.opcion || null;
  const op = opciones.find((o) => o.opcion === opcionActual) || null;
  const termina = override || op?.termina_si_activas || null;

  // Si lo que había para activar ya no está (se activó en otra pestaña, venció el saldo), no queda nada que confirmar.
  useEffect(() => {
    if (abierto && jornada && !opciones.length) setAbierto(false);
  }, [abierto, jornada, opciones.length]);

  if (!abierto || !opciones.length) return null;

  const cancelar = () => setAbierto(false);
  const { plan, n12, n24 } = saldoDe(jornada);
  const quedanDe = (o) => (o.opcion === "plan" ? o.quedan ?? saldoDe(jornada).delPlan : o.quedan ?? (o.opcion === "12h" ? n12 : n24));
  const venceTxt = (o) => (o.vence_en ? diaMes(o.vence_en) : "");

  const ENCIMA = { zIndex: Z_ENCIMA_DEL_GATE };

  if (!opcionActual) {
    return (
      <Dialog open onClose={cancelar} sx={ENCIMA} PaperProps={{ "data-testid": "jornada-elegir", sx: paperSx }}>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>Elige qué jornada activar</Typography>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: "var(--text-secondary)", mb: 1.5 }}>
          Elegir no descuenta nada: antes de activar ves la hora y confirmas.
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {opciones.map((o) => {
            const quedan = quedanDe(o);
            const vence = venceTxt(o);
            const detalle =
              o.opcion === "plan"
                ? `De tu plan: te quedan ${quedan} de ${plan}`
                : `${quedan === 1 ? "Te queda 1" : `Te quedan ${quedan}`}${vence ? `, ${quedan === 1 ? "vence" : "vencen"} el ${vence}` : ""}`;
            return (
              <Box
                key={o.opcion}
                component="button"
                type="button"
                data-testid={`jornada-opcion-${o.opcion}`}
                onClick={() => {
                  setElegida(o.opcion);
                  setOverride(null);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  p: 1.25,
                  pl: 1.5,
                  borderRadius: "13px",
                  fontFamily: FUENTE,
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                  border: "1px solid rgba(var(--fg-rgb),0.16)",
                  backgroundColor: "rgba(var(--fg-rgb),0.03)",
                  "&:hover": { borderColor: "var(--color-primary)", backgroundColor: "rgba(var(--color-primary-rgb),0.08)" },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25 }}>{`Jornada de ${horasDe(o.opcion)}\u00a0h`}</Box>
                  <Box sx={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.3, mt: 0.25 }}>{detalle}</Box>
                </Box>
                <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                  <Box sx={{ fontSize: 11.5, color: "var(--text-secondary)", lineHeight: 1.2 }}>hasta las</Box>
                  <Box data-testid={`jornada-opcion-${o.opcion}-hora`} sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                    {hora(o.termina_si_activas)}
                  </Box>
                  <Box sx={{ fontSize: 11.5, color: "var(--text-secondary)", lineHeight: 1.2 }}>{cuando(o.termina_si_activas, ahora)}</Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
        <Button data-testid="jornada-confirmar-no" onClick={cancelar} sx={{ ...ghostSx, minHeight: 40, width: "100%" }}>
          Cancelar
        </Button>
      </Dialog>
    );
  }

  if (!termina) return null;
  const horas = horasDe(opcionActual);
  const fin = new Date(termina);
  const salto = offsetMin(fin) - offsetMin(ahora);
  const horaSinSalto = hora(new Date(fin.getTime() - salto * 60000).toISOString());
  const resto = (tipo) => guardadas(tipo === "12h" ? n12 - 1 : n12, tipo === "24h" ? n24 - 1 : n24) || "ninguna";

  const activar = async () => {
    setEnviando(true);
    setError("");
    try {
      await activarJornada({ opcion: opcionActual, terminaEnVisto: termina });
      setAbierto(false);
      recargar();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.code === "hora_cambio" && d.termina_si_activas) {
        setOverride(d.termina_si_activas);
        setCambio(true);
      } else {
        setError(MENSAJES[d?.code] || "No pudimos activar la jornada. Intenta de nuevo; no se descontó nada.");
        recargar();
      }
    } finally {
      setEnviando(false);
    }
  };

  const descuento =
    opcionActual === "plan"
      ? `Descuenta 1 jornada de 12\u00a0h de tu plan: te quedarán ${Math.max((op?.quedan ?? saldoDe(jornada).delPlan) - 1, 0)} de ${plan} este mes.`
      : `Descuenta 1 jornada de ${horas}\u00a0h de tus jornadas adicionales. Guardadas después: ${resto(opcionActual)}.`;

  return (
    <Dialog open onClose={cancelar} sx={ENCIMA} PaperProps={{ "data-testid": "jornada-confirmar", sx: paperSx }}>
      <Typography sx={{ fontFamily: FUENTE, fontSize: 17, fontWeight: 700, color: "var(--text-primary)", mb: 1.5 }}>{`Activar jornada de ${horas} horas`}</Typography>

      {cambio ? (
        <Recuadro testId="jornada-hora-cambio">
          Pasaron unos minutos desde que abriste esta ventana, así que la hora cambió. Revísala antes de activar. Todavía no se descuenta nada.
        </Recuadro>
      ) : null}

      <HoraGrande iso={termina} ahora={ahora} horas={horas} />

      {salto !== 0 ? (
        <Recuadro testId="jornada-cambio-de-hora">
          {`Antes de que termine cambia la hora en Chile: el reloj se ${salto < 0 ? "atrasa" : "adelanta"} ${Math.abs(salto) / 60} hora. Por eso termina a las ${hora(termina)} y no a las ${horaSinSalto}. Son las mismas ${horas + 1} horas.`}
        </Recuadro>
      ) : null}

      <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.25 }}>
        <Punto>Después de esa hora puedes seguir leyendo, copiando, editando y guardando, pero sin IA.</Punto>
        {opcionActual === "24h" ? <Punto>{"Se usa entera: no se parte en dos jornadas de 12\u00a0h."}</Punto> : null}
        <Punto>{descuento}</Punto>
        <Punto>No se devuelve, aunque no generes ningún documento.</Punto>
      </Box>

      {error ? (
        <Typography role="alert" data-testid="jornada-confirmar-error" sx={{ fontFamily: FUENTE, fontSize: 12.5, color: "var(--color-danger)", mb: 1.25 }}>
          {error}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ "& > button": { flex: 1 } }}>
        {opciones.length > 1 ? (
          <Button data-testid="jornada-confirmar-volver" onClick={() => {
              setElegida(null);
              setOverride(null);
            }} sx={{ ...ghostSx, minHeight: 40 }}>
            Volver
          </Button>
        ) : (
          <Button data-testid="jornada-confirmar-no" onClick={cancelar} sx={{ ...ghostSx, minHeight: 40 }}>
            Cancelar
          </Button>
        )}
        <Button data-testid="jornada-confirmar-si" disabled={enviando} onClick={activar} sx={{ ...primarySx, minHeight: 40 }}>
          {`Activar hasta las ${hora(termina)}`}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default JornadaConfirmarDialog;
